import React, {ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import MimeTypes from "../../document/mimetype/MimeTypes";
import Resizeable, {ResizeableDirection} from "../../../shared/components/Resizeable";
import LengthUnit from "../../../shared/units/LengthUnit";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import ViewerType from "../../document/DataType";
import MimeType from "../../document/mimetype/MimeType";
import PdfViewer, {PdfViewerDefinition} from "./PdfViewer";
import {ViewerMode} from "../ViewerMode";
import ImageViewer, {ImageViewerDefinition} from "../image/ImageViewer";
import TiffViewer, {TiffViewerDefinition} from "../image/TiffViewer";
import HeicViewer, {HeicViewerDefinition} from "../image/HeicViewer";
import FileHelper, {PATH_SEPARATOR} from "../../../shared/helper/FileHelper"
import {pdfjs} from "react-pdf";
import PortalDocumentManager from "../../document/PortalDocumentManager";
import {PortalGlobals} from "../../Portal";
import Parameter from "../../dialog/parameter/Parameter";
import DialogDefault from "../../dialog/parameter/PortalDialogDefault";
import PortalDialogType from "../../dialog/PortalDialogType";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {PortfolioRemoveDialogDefinition} from "../../dialog/components/portfolio/PortfolioRemoveDialog";
import {
	Attachment,
	AttachmentsDocument,
	BaseToolbox,
	Folder,
	FoldersDocument,
	Information,
	PortfolioExtract,
	PortfolioRemove,
	PortfolioSelection,
	Settings,
	SettingsInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Exception from "../../../shared/exception/Exception";
import ErrorCode from "../../../shared/exception/ErrorCode";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import MenuButton from "../../../shared/menu/MenuButton";
import {PortalToolboxPortfolioPortfolio} from "../../../shared/models/PortalToolboxPortfolioPortfolio";
import {PortalPortfolioAdd} from "../../../shared/models/PortalPortfolioAdd";
import Immutable from "../../../shared/immutable/Immutable";
import DocumentSettings from "../../document/DocumentSettings";
import TextViewer, {TextViewerDefinition} from "../text/TextViewer";
import ZipViewer, {ZipViewerDefinition} from "../zip/ZipViewer";
import HtmlViewer, {HtmlViewerDefinition} from "../html/HtmlViewer";
import MimeTypeFilter from "../../document/mimetype/MimeTypeFilter";
import Translate from "../../../shared/translation/Translate";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {PortfolioAddFileDialogDefinition} from "../../dialog/components/portfolio/PortfolioAddFileDialog";
import {PortfolioAddFolderDialogDefinition} from "../../dialog/components/portfolio/PortfolioAddFolderDialog";
import LoadingViewer from "../LoadingViewer";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

const urlCreator = FunctionHelper.applyValue(window.URL, window.webkitURL);

export interface PortfolioListProperties extends I18nGlobals, PortalGlobals, DebugGlobals {
	viewerScreen: RefObject<HTMLDivElement>;
	portalDocument?: Immutable<PortalDocumentInterface>;
}

export interface PortfolioListState {
	portfolioListWidth: string;
	currentFolder?: string;
	selection?: string;
}

export default class PortfolioList extends PortalPureComponent<PortfolioListProperties, PortfolioListState> {
	/**
	 * Constructs a {@link PortfolioList} constructor
	 */
	constructor(props: Readonly<PortfolioListProperties>) {
		super(props);

		this.state = {
			portfolioListWidth: 350 + LengthUnit.PIXEL
		};

		this.goHome = this.goHome.bind(this);
		this.goBack = this.goBack.bind(this);
		this.downloadFile = this.downloadFile.bind(this);
		this.addFile = this.addFile.bind(this);
		this.addFolder = this.addFolder.bind(this);
		this.removeFile = this.removeFile.bind(this);
		this.extractFile = this.extractFile.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();
		this.applyDocumentSettings();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PortfolioListProperties>, prevState: Readonly<PortfolioListState>, snapshot?: any
	): Promise<void> {
		this.applyDocumentSettings();
	}

	private applyDocumentSettings() {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		this.setState({
			selection: documentSettings.portfolioSelection,
			currentFolder: documentSettings.portfolioFolder
		});
	}

	private updateDocumentSettings() {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		documentSettings.portfolioSelection = this.state.selection;
		documentSettings.portfolioFolder = this.state.currentFolder;

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		documentManager.updateGlobalDocumentList();
	}

	private goHome() {
		this.setState({
			selection: undefined,
			currentFolder: undefined
		}, this.updateDocumentSettings);
	}

	private goBack() {
		let folderPath: string | undefined;

		if (FunctionHelper.exists(this.props.portalDocument)) {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument!.get().id!
			);

			let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
				FunctionHelper.applyValue(this.state.currentFolder, "")
			);

			folderPath = FunctionHelper.applyValue(
				currentFolder?.path, Folder.getPathDefault()
			).split(PATH_SEPARATOR).slice(0, -1).join(PATH_SEPARATOR);
		}

		this.setState({
			selection: undefined,
			currentFolder: folderPath !== "" ? folderPath : undefined
		}, this.updateDocumentSettings)
	}

	private async downloadFile(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let pdfDocument: pdfjs.PDFDocumentProxy;

		try {
			pdfDocument = await portalDocument.loadViewerData();
		} catch (e: any) {
			return;
		}

		let attachment: Attachment | undefined = portalDocument.getAttachment(
			FunctionHelper.applyValue(this.state.selection, "")
		);
		if (!FunctionHelper.exists(attachment)) {
			return;
		}

		let attachmentContent: BlobPart | undefined;

		if (FunctionHelper.exists(attachment!.page) && attachment!.page !== 0) {
			let pageProxy: pdfjs.PDFPageProxy = await pdfDocument.getPage(attachment!.page!);
			let annotations: Array<any> = await pageProxy.getAnnotations();

			for (let annotation of annotations) {
				if (annotation.subtype === 'FileAttachment' && annotation.file.filename === attachment!.name) {
					attachmentContent = annotation.file.content;
					break;
				}
			}
		} else {
			let globalAttachments: any = await pdfDocument.getAttachments();

			for (let key in globalAttachments) {
				if (globalAttachments.hasOwnProperty(key) && globalAttachments[key].filename === attachment!.name) {
					attachmentContent = globalAttachments[key].content;
					break;
				}
			}
		}

		if (!FunctionHelper.exists(attachmentContent)) {
			return;
		}

		let fileBlob: Blob = new Blob([attachmentContent!], {type: attachment!.mimeType});

		if (FunctionHelper.exists(urlCreator.createObjectURL)) {
			let blobUrl: string = urlCreator.createObjectURL(fileBlob);
			await FileHelper.download([{
				name: FunctionHelper.applyValue(attachment!.name, "unknown"), url: blobUrl
			}]);
			urlCreator.revokeObjectURL(blobUrl);
		}
	}

	private addFile(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let currentPath: string = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR).join(PATH_SEPARATOR);

		let selection: Array<PortalDocument> = documentManager.getSelection();
		let parameter: Parameter<PortalToolboxPortfolioPortfolio> =
			DialogDefault.getParameter(PortalDialogType.PORTFOLIO_ADD_FILE, selection);
		let addParameter: PortalPortfolioAdd = parameter.data.add as PortalPortfolioAdd;
		addParameter.path = currentPath;

		this.props.context.getPage().setState({
			showPortfolioAddFileDialog: true,
			portfolioAddFileParameter: parameter
		});
	}

	private addFolder(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let currentPath: string = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR).join(PATH_SEPARATOR);

		let selection: Array<PortalDocument> = documentManager.getSelection();
		let parameter: Parameter<PortalToolboxPortfolioPortfolio> =
			DialogDefault.getParameter(PortalDialogType.PORTFOLIO_ADD_FOLDER, selection);
		let addParameter: PortalPortfolioAdd = parameter.data.add as PortalPortfolioAdd;
		addParameter.path = currentPath;

		this.props.context.getPage().setState({
			showPortfolioAddFolderDialog: true,
			portfolioAddFolderParameter: parameter
		});
	}

	private removeFile(): void {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let parameter: Parameter<PortalToolboxPortfolioPortfolio> =
			DialogDefault.getParameter(PortalDialogType.PORTFOLIO_REMOVE, selection);
		let removeParameter: PortfolioRemove = parameter.data.remove as PortfolioRemove;
		let fileSelection: PortfolioSelection = PortfolioSelection.fromJson({});

		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let currentPath: string = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR).join(PATH_SEPARATOR);

		let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
			FunctionHelper.applyValue(this.state.selection, "")
		);
		let selectedFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.selection, "")
		);

		if (FunctionHelper.exists(selectedAttachment)) {
			fileSelection.fileName = selectedAttachment!.name!;
			fileSelection.path = currentPath;
		} else if (FunctionHelper.exists(selectedFolder)) {
			fileSelection.fileName = "";
			fileSelection.path = selectedFolder!.path!;
		}

		removeParameter.selection = [fileSelection];

		this.props.context.getPage().setState({
			showPortfolioRemoveDialog: true,
			portfolioRemoveParameter: parameter
		});
	}

	private async extractFile(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let parameter: Parameter<PortalToolboxPortfolioPortfolio> =
			DialogDefault.getParameter(PortalDialogType.PORTFOLIO_EXTRACT, selection);
		let extractParameter: PortfolioExtract = parameter.data.extract as PortfolioExtract;
		let fileSelection: PortfolioSelection = PortfolioSelection.fromJson({});

		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let currentPath: string = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR).join(PATH_SEPARATOR);

		let selectedAttachment: Attachment | undefined = portalDocument.getAttachment(
			FunctionHelper.applyValue(this.state.selection, "")
		);
		let selectedFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.selection, "")
		);

		if (FunctionHelper.exists(selectedAttachment)) {
			fileSelection.fileName = selectedAttachment!.name!;
			fileSelection.path = currentPath;
		} else if (FunctionHelper.exists(selectedFolder)) {
			fileSelection.fileName = "*.*";
			fileSelection.path = selectedFolder!.path! + PATH_SEPARATOR + "*";
		}

		extractParameter.selection = [fileSelection];

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			portfolio: parameter.data
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				if (portalException.code === ErrorCode.WEBSERVICE_SESSION_TIMEOUT) {
					await this.extractFile();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.portfolio.extract", MessageType.SUCCESS);
			this.props.context.getPage().addMessage(message);
		}
	}

	/**
	 * render the preview for an attachment list item
	 */
	private renderPreview(file: Attachment): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return <span className="icon icon-page"/>;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let mimeType: MimeType = portalDocument.getMimeType(file);
		let viewerType: ViewerType = portalDocument.getViewerType(file);

		if (MimeTypeFilter.checkSelection([mimeType], [MimeTypes.DOC, MimeTypes.DOCX])) {
			viewerType = ViewerType.DEFAULT;
		}

		if (portalDocument.isLocked()) {
			return <LoadingViewer {...this.props} viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
		}

		switch (viewerType) {
			case ViewerType.PDF:
				return <PdfViewer {...PdfViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								  viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.IMAGE:
				return <ImageViewer {...ImageViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
									viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.TIFF:
				return <TiffViewer {...TiffViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.HEIC:
				return <HeicViewer {...HeicViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.TEXT:
				return <TextViewer {...TextViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.ZIP:
				return <ZipViewer {...ZipViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								  viewerMode={ViewerMode.PORTFOLIO_PAGE}/>;
			case ViewerType.HTML:
				return <HtmlViewer {...HtmlViewerDefinition.defaults()} {...this.props} attachmentKey={file.objectKey}
								   viewerMode={ViewerMode.PORTFOLIO_PAGE} viewAsHTML={true}/>;
		}

		return <span className="icon icon-page"/>;
	}

	private renderPortfolioPath(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let folders: Array<Folder> = FunctionHelper.applyValue(
			portalDocument.metadata?.folders?.folder, FoldersDocument.getFolderDefault()
		);

		let path: Array<ReactNode> = [];
		let parts: Array<string> = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR);
		let backPath: string = "";
		for (let part of parts) {
			if (FunctionHelper.isEmpty(part)) {
				continue;
			}

			let folderPath: string = Folder.getPathDefault();
			backPath += (!FunctionHelper.isEmpty(backPath) ? PATH_SEPARATOR : "") + part;

			for (let folder of folders) {
				if (folder.path === backPath) {
					folderPath = FunctionHelper.applyValue(folder.path, Folder.getPathDefault());
				}
			}

			path.push(
				<a href={"portfolio"} key={folderPath} onClick={(event) => {
					event.preventDefault();

					this.setState({
						selection: undefined,
						currentFolder: folderPath !== "" ? folderPath : undefined
					}, this.updateDocumentSettings)
				}}>
					{part + PATH_SEPARATOR}
				</a>
			);
		}

		return (
			<p className="portfolio-path">
				<a href={"portfolio"} onClick={(event) => {
					event.preventDefault();
					this.goHome()
				}}>{PATH_SEPARATOR}</a>
				{path}
			</p>
		);
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let portalInfo: Information = this.props.context.info;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let portalDocument: PortalDocument = documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (
			!portalDocument.isPortfolio() || portalDocument.getMimeType() !== MimeTypes.PDF ||
			!portalDocument.isViewable() || portalDocument.isLocked()
		) {
			return null;
		}

		let currentFolder: Folder | undefined = portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.currentFolder, "")
		);
		let currentPath: string = FunctionHelper.applyValue(
			currentFolder?.path, Folder.getPathDefault()
		).split(PATH_SEPARATOR).join(PATH_SEPARATOR);

		let folders: Array<Folder> = FunctionHelper.applyValue(
			portalDocument.metadata?.folders?.folder, FoldersDocument.getFolderDefault()
		);
		let folderElements: Array<ReactNode> = [];
		for (let folder of folders) {
			let path: string = FunctionHelper.applyValue(
				folder.path, Folder.getPathDefault()
			);
			let folderPath: string = path.split(PATH_SEPARATOR).slice(0, -1).join(PATH_SEPARATOR);

			if (path === "" || currentPath !== folderPath) {
				continue;
			}

			folderElements.push(
				<div className={"portfolio-folder" + (this.state.selection === path ? " selected" : "")} key={path}
					 onDoubleClick={(event: React.MouseEvent) => {
						 this.setState({
							 selection: undefined,
							 currentFolder: path
						 }, this.updateDocumentSettings);
					 }}
					 onClick={(event: React.MouseEvent) => {
						 event.stopPropagation();

						 this.setState({
							 selection: path
						 }, this.updateDocumentSettings);
					 }}>
					<div className="preview">
						<MenuButton {...this.props} icon="icon icon-folder"
									tooltip={Translate.value("portfolio.open", {ns: "sidebar"})}
									onClick={(event: React.MouseEvent) => {
										event.stopPropagation();

										this.setState({
											selection: undefined,
											currentFolder: path
										}, this.updateDocumentSettings);
									}}/>
					</div>
					<div className="name">{folder.name}</div>
				</div>
			);
		}

		let files: Array<Attachment> = FunctionHelper.applyValue(
			portalDocument.metadata?.attachments?.attachment, AttachmentsDocument.getAttachmentDefault()
		);
		let filesElements: Array<ReactNode> = [];
		for (let file of files) {
			if (file.folderId !== FunctionHelper.applyValue(currentFolder?.id, Folder.getIdDefault())) {
				continue;
			}

			filesElements.push(
				<div className={"portfolio-file" + (this.state.selection === file.objectKey ? " selected" : "")}
					 key={file.objectKey}
					 onClick={(event: React.MouseEvent) => {
						 event.stopPropagation();

						 this.setState({
							 selection: file.objectKey
						 }, this.updateDocumentSettings);
					 }}>
					<div className="preview">
						{this.renderPreview(file)}
					</div>
					<div className="name">{file.name}</div>
				</div>
			);
		}

		let isFolderSelected: boolean = FunctionHelper.exists(portalDocument.getFolderByPath(
			FunctionHelper.applyValue(this.state.selection, "")
		));

		return (
			<div className="portfolio-list" style={{width: this.state.portfolioListWidth}}
				 data-help="global.sidebar.portfolio" data-position="right">
				<Resizeable direction={ResizeableDirection.HORIZONTAL}
							resizeCallback={(options: { width: string }) => {
								this.setState({
									portfolioListWidth: options.width
								});
							}}
							container={this.props.viewerScreen} invert={false}/>
				<div className="body">
					<div className="toolbar">
						<MenuButton {...this.props} disabled={!FunctionHelper.exists(this.state.currentFolder)}
									onClick={this.goHome} icon="icon icon-folder home"
									tooltip={Translate.value("portfolio.home", {ns: "sidebar"})}/>
						<MenuButton {...this.props} disabled={!FunctionHelper.exists(this.state.currentFolder)}
									onClick={this.goBack} icon="icon icon-left"
									tooltip={Translate.value("portfolio.back", {ns: "sidebar"})}/>
						<div className="separator"/>
						{(!this.props.documentChecks || PortfolioAddFileDialogDefinition.isEnabled(portalInfo)) &&
							<MenuButton {...this.props} onClick={this.addFile} icon="icon icon-add file"
										disabled={
											this.props.documentChecks &&
											!PortfolioAddFileDialogDefinition.isActive(
												selection, this.props.context.info, this.props.signatureEditProfile
											)
										}
										tooltip={Translate.value("portfolio.add.file", {ns: "sidebar"})}/>
						}
						{(!this.props.documentChecks || PortfolioAddFolderDialogDefinition.isEnabled(portalInfo)) &&
							<MenuButton {...this.props} onClick={this.addFolder} icon="icon icon-add folder"
										disabled={
											this.props.documentChecks &&
											!PortfolioAddFolderDialogDefinition.isActive(
												selection, this.props.context.info, this.props.signatureEditProfile
											)
										}
										tooltip={Translate.value("portfolio.add.folder", {ns: "sidebar"})}/>
						}
						{(!this.props.documentChecks || PortfolioRemoveDialogDefinition.isEnabled(portalInfo)) &&
							<MenuButton {...this.props} icon="icon icon-delete small" onClick={this.removeFile}
										disabled={
											this.props.documentChecks &&
											(
												!PortfolioRemoveDialogDefinition.isActive(
													selection, this.props.context.info, this.props.signatureEditProfile
												) || !FunctionHelper.exists(this.state.selection)
											)
										}
										tooltip={Translate.value("portfolio.remove", {ns: "sidebar"})}/>
						}
						{
							(
								!this.props.documentChecks ||
								FunctionHelper.applyValue((
									portalInfo.webserviceStatus?.webservices?.toolbox?.enabled &&
									portalInfo.functionsStatus?.portfolio
								), false)
							) &&
							<MenuButton {...this.props} icon="icon icon-export small" onClick={this.extractFile}
										disabled={
											this.props.documentChecks && !FunctionHelper.exists(this.state.selection)
										}
										tooltip={Translate.value("portfolio.extract", {ns: "sidebar"})}/>
						}
						<MenuButton {...this.props} icon="icon icon-download small" onClick={this.downloadFile}
									tooltip={Translate.value("portfolio.download", {ns: "sidebar"})}
									disabled={
										this.props.documentChecks &&
										(!FunctionHelper.exists(this.state.selection) || isFolderSelected)
									}/>
					</div>
					<div className="content" onClick={() => {
						this.setState({
							selection: undefined
						}, this.updateDocumentSettings);
					}}>
						<div className="container">
							{folderElements}
							{filesElements}
							{folderElements.length === 0 && filesElements.length === 0 &&
								<p className="no-results">
									<Translate {...this.props} data="portfolio.empty" namespace="sidebar"/>
								</p>
							}
						</div>
					</div>
					<div className="toolbar inverted">
						{this.renderPortfolioPath()}
					</div>
				</div>
			</div>
		);
	}
}